import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { formatTimeLeft, formatDateTime } from '@/lib/ctrader-parsers';

interface CtraderAccount {
  id: number;
  account_id: string;
  account_name: string;
  balance: number;
  currency: string;
  is_live: boolean;
}

interface GatewayStatus {
  connected: boolean;
  endpoint: string | null;
  clientId: string | null;
  errorMessage: string | null;
  expiresAt: string | null;
  updatedAt: string | null;
  lastWsRaw: string | null;
  accounts: CtraderAccount[];
}

export default function CtraderConnection() {
  const navigate = useNavigate();
  const [environment, setEnvironment] = useState<'demo' | 'live'>('demo');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [accounts, setAccounts] = useState<CtraderAccount[]>([]);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [lastWsRaw, setLastWsRaw] = useState<string | null>(null);
  const [lastPayloadKeys, setLastPayloadKeys] = useState<string | null>(null);
  const [lastLogSummary, setLastLogSummary] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [showSecretInput, setShowSecretInput] = useState(true);
  const [showDebugPanel, setShowDebugPanel] = useState(false);

  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackProcessedRef = useRef(false);
  const mountedRef = useRef(true);
  const refreshInProgressRef = useRef(false);
  const refreshSafetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastRefreshTimeRef = useRef(0);
  const autoRefreshDisabledRef = useRef(false);

  const REFRESH_THRESHOLD_MS = 5 * 60 * 1000;
  const MIN_REFRESH_INTERVAL_MS = 60 * 1000;
  const MIN_AUTO_REFRESH_DELAY_MS = 30 * 1000;

  const redirectUri = typeof window !== 'undefined'
    ? window.location.origin + window.location.pathname
    : '';

  const proxyUrl = environment === 'live'
    ? 'wss://live.ctraderapi.com:5036'
    : 'wss://demo.ctraderapi.com:5036';

  const fetchStatus = useCallback(async () => {
    if (!mountedRef.current) return;
    try {
      const { data } = await supabase.functions.invoke<GatewayStatus>('ctrader-gateway', {
        method: 'POST',
        body: { action: 'status' },
      });
      if (!mountedRef.current) return;
      if (data) {
        setIsConnected(data.connected);
        setAccounts(data.accounts || []);
        setExpiresAt(data.expiresAt);
        setUpdatedAt(data.updatedAt);
        setLastWsRaw(data.lastWsRaw || null);
        if (data.clientId) setClientId(data.clientId);
        if (data.errorMessage && !data.connected) setError(data.errorMessage);
      }
    } catch { /* ignore */ }
    if (mountedRef.current) setLoading(false);
  }, []);

  const clearRefreshSafetyTimer = useCallback(() => {
    if (refreshSafetyTimerRef.current) {
      clearTimeout(refreshSafetyTimerRef.current);
      refreshSafetyTimerRef.current = null;
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    const now = Date.now();
    if (now - lastRefreshTimeRef.current < MIN_REFRESH_INTERVAL_MS) return;
    if (refreshInProgressRef.current) return;
    refreshInProgressRef.current = true;
    lastRefreshTimeRef.current = now;
    clearRefreshSafetyTimer();
    setRefreshLoading(true);
    setError('');

    refreshSafetyTimerRef.current = setTimeout(() => {
      setRefreshLoading(false);
      refreshInProgressRef.current = false;
    }, 25000);

    try {
      const { data, error: fnError } = await supabase.functions.invoke<{
        success: boolean;
        expiresAt: string;
        accounts: CtraderAccount[];
        error?: string;
        debugRaw?: string;
        payloadKeys?: string;
        logSummary?: string;
      }>('ctrader-gateway', {
        method: 'POST',
        body: { action: 'refresh' },
      });
      if (!mountedRef.current) return;
      if (fnError || !data?.success) {
        setError(data?.error || fnError?.message || 'Token refresh failed');
        if (!data?.success) { setIsConnected(false); setAccounts([]); setExpiresAt(null); }
        return;
      }
      setExpiresAt(data.expiresAt);
      setAccounts(data.accounts || []);
      setLastWsRaw(data.debugRaw || null);
      setLastPayloadKeys(data.payloadKeys || null);
      setLastLogSummary(data.logSummary || null);
      setSuccess('Token refreshed — ' + (data.accounts?.length || 0) + ' account(s)');
      setTimeout(() => { if (mountedRef.current) setSuccess(''); }, 4000);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : 'Refresh failed');
    } finally {
      setRefreshLoading(false);
      clearRefreshSafetyTimer();
      refreshInProgressRef.current = false;
    }
  }, [clearRefreshSafetyTimer]);

  useEffect(() => {
    mountedRef.current = true;
    setRefreshLoading(false);
    refreshInProgressRef.current = false;
    lastRefreshTimeRef.current = 0;
    autoRefreshDisabledRef.current = false;
    clearRefreshSafetyTimer();
    return () => {
      mountedRef.current = false;
      clearRefreshSafetyTimer();
    };
  }, [clearRefreshSafetyTimer]);

  // OAuth callback handling
  useEffect(() => {
    if (callbackProcessedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const urlCode = params.get('code');
    const savedCode = localStorage.getItem('ctrader_pending_oauth_code');
    const code = urlCode || savedCode;

    if (code) {
      callbackProcessedRef.current = true;
      window.history.replaceState({}, '', window.location.origin + window.location.pathname);
      localStorage.removeItem('ctrader_pending_oauth_code');

      const savedEnv = localStorage.getItem('ctrader_env') || 'demo';
      const savedClientId = localStorage.getItem('ctrader_client_id') || '';
      const savedClientSecret = localStorage.getItem('ctrader_client_secret') || '';

      if (savedClientId && savedClientSecret) {
        setEnvironment(savedEnv as 'demo' | 'live');
        setClientId(savedClientId);
        setClientSecret('');
        setShowSecretInput(false);
        handleOAuthCallback(code, savedClientId, savedClientSecret, savedEnv as 'demo' | 'live');
      } else {
        setError('OAuth callback received but no saved credentials found.');
        setLoading(false);
      }
    } else {
      fetchStatus();
    }
  }, [fetchStatus]);

  // Countdown
  useEffect(() => {
    if (!expiresAt || !isConnected) return;
    const tick = () => {
      const remaining = new Date(expiresAt).getTime() - Date.now();
      setTimeLeft(Math.max(0, remaining));
    };
    tick();
    countdownRef.current = setInterval(tick, 1000);
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, [expiresAt, isConnected]);

  // Auto-refresh
  useEffect(() => {
    if (!expiresAt || !isConnected) return;
    const expiresMs = new Date(expiresAt).getTime();
    const now = Date.now();
    const refreshAt = expiresMs - REFRESH_THRESHOLD_MS;
    let delay = Math.max(0, refreshAt - now);
    if (now >= expiresMs) { autoRefreshDisabledRef.current = true; return; }
    if (delay < MIN_AUTO_REFRESH_DELAY_MS) delay = MIN_AUTO_REFRESH_DELAY_MS;
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => { handleRefresh(); }, delay);
    return () => { if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current); };
  }, [expiresAt, isConnected, handleRefresh]);

  const handleOAuthCallback = async (code: string, cId: string, cSecret: string, env: 'demo' | 'live') => {
    setLoading(false);
    setConnecting(true);
    setError('');
    setSuccess('');
    const thisProxyUrl = env === 'live' ? 'wss://live.ctraderapi.com:5036' : 'wss://demo.ctraderapi.com:5036';

    try {
      const { data, error: fnError } = await supabase.functions.invoke<{
        success: boolean;
        connected: boolean;
        endpoint: string;
        clientId: string;
        expiresAt: string;
        accounts: CtraderAccount[];
        error?: string;
        warning?: string;
        debugRaw?: string;
        payloadKeys?: string;
        logSummary?: string;
      }>('ctrader-gateway', {
        method: 'POST',
        body: {
          action: 'oauth-callback',
          code,
          redirectUri,
          clientId: cId,
          clientSecret: cSecret,
          proxyUrl: thisProxyUrl,
        },
      });

      if (!mountedRef.current) return;

      if (fnError) {
        const msg = typeof fnError === 'object' && fnError !== null
          ? (fnError as Record<string, unknown>).message as string || JSON.stringify(fnError)
          : String(fnError);
        setError('Edge function error: ' + msg);
        setConnecting(false);
        return;
      }

      if (!data) { setError('Edge function returned empty response.'); setConnecting(false); return; }
      if (!data.success) { setError(data.error || 'OAuth callback failed'); setConnecting(false); fetchStatus(); return; }

      setIsConnected(true);
      setAccounts(data.accounts || []);
      setExpiresAt(data.expiresAt);
      setUpdatedAt(new Date().toISOString());
      setLastWsRaw(data.debugRaw || null);
      setLastPayloadKeys(data.payloadKeys || null);
      setLastLogSummary(data.logSummary || null);
      setEnvironment(env);

      if (data.accounts && data.accounts.length > 0) {
        setSuccess('Connected to cTrader — ' + data.accounts.length + ' account(s). Redirecting to Strategies...');
        setTimeout(() => { if (mountedRef.current) navigate('/strategies'); }, 1500);
      } else {
        setSuccess('Connected to cTrader, but no trading accounts returned.');
      }

      if (data.warning) setError(data.warning);
      setTimeout(() => { if (mountedRef.current) setSuccess(''); }, 8000);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : 'Unexpected error');
    } finally {
      if (mountedRef.current) setConnecting(false);
    }
  };

  const handleAuthorize = () => {
    setError('');
    setSuccess('');
    if (!clientId.trim() || !clientSecret.trim()) {
      setError('Please enter both Client ID and Client Secret');
      return;
    }
    localStorage.setItem('ctrader_env', environment);
    localStorage.setItem('ctrader_client_id', clientId.trim());
    localStorage.setItem('ctrader_client_secret', clientSecret.trim());

    const scopeValue = 'trading';
    const authUrl = 'https://id.ctrader.com/my/settings/openapi/grantingaccess/' +
      '?client_id=' + encodeURIComponent(clientId.trim()) +
      '&redirect_uri=' + encodeURIComponent(redirectUri) +
      '&scope=' + encodeURIComponent(scopeValue) +
      '&product=web';

    window.location.href = authUrl;
  };

  const handleDisconnect = async () => {
    try {
      await supabase.functions.invoke('ctrader-gateway', { method: 'POST', body: { action: 'disconnect' } });
      if (!mountedRef.current) return;
      setIsConnected(false);
      setAccounts([]);
      setExpiresAt(null);
      setUpdatedAt(null);
      setTimeLeft(0);
      setShowSecretInput(true);
      setLastWsRaw(null);
      setLastPayloadKeys(null);
      setLastLogSummary(null);
      localStorage.removeItem('ctrader_env');
      localStorage.removeItem('ctrader_client_id');
      localStorage.removeItem('ctrader_client_secret');
      setSuccess('Disconnected from cTrader API');
      setTimeout(() => { if (mountedRef.current) setSuccess(''); }, 3000);
    } catch { /* ignore */ }
  };

  const isNearExpiry = expiresAt ? new Date(expiresAt).getTime() - Date.now() <= REFRESH_THRESHOLD_MS && new Date(expiresAt).getTime() - Date.now() > 0 : false;
  const isExpired = expiresAt ? new Date(expiresAt).getTime() - Date.now() <= 0 : false;

  // ─── Render ─────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="bg-background-100 rounded-xl border border-background-200/70 p-5">
        <div className="flex items-center justify-center py-6">
          <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (connecting) {
    return (
      <div className="bg-background-100 rounded-xl border border-background-200/70 p-5">
        <div className="flex flex-col items-center justify-center py-10 gap-3">
          <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-foreground-700 text-sm font-medium">Completing cTrader OAuth...</p>
          <p className="text-foreground-500 text-xs">Exchanging authorization code for access token</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-sm font-heading font-semibold text-foreground-900 mb-1">cTrader API Gateway</h2>
        <p className="text-foreground-500 text-xs">
          Connect via cTrader Open API OAuth 2.0. Once authorized, manage live data on the Strategies page.
        </p>
      </div>

      {/* ── Not Connected: Auth form ─────────────────────────────── */}
      {!isConnected && (
        <div className="bg-background-100 rounded-xl border border-background-200/70 p-5">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center">
              <i className="ri-exchange-funds-line text-primary-400 text-sm"></i>
            </div>
            <div>
              <h3 className="text-sm font-heading font-semibold text-foreground-900">Connect with cTrader</h3>
              <p className="text-foreground-500 text-xs mt-0.5">Enter your cTrader Open API app credentials to authorize</p>
            </div>
          </div>

          {/* Environment */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-foreground-600 mb-2">Environment</label>
            <div className="flex gap-2 bg-background-50 rounded-lg border border-background-300/60 p-1 w-fit">
              <button
                type="button"
                onClick={() => { setEnvironment('demo'); setError(''); }}
                className={`px-4 py-2 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${environment === 'demo' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-foreground-900'}`}
              >
                <i className="ri-computer-line mr-1.5"></i>Demo
              </button>
              <button
                type="button"
                onClick={() => { setEnvironment('live'); setError(''); }}
                className={`px-4 py-2 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${environment === 'live' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-foreground-900'}`}
              >
                <i className="ri-global-line mr-1.5"></i>Live
              </button>
            </div>
            <p className="text-foreground-500 text-[11px] mt-1.5">
              Proxy: <code className="text-foreground-700 bg-background-200/50 px-1 rounded">{proxyUrl}</code>
            </p>
          </div>

          {/* Credentials */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Client ID</label>
              <input
                type="text"
                value={clientId}
                onChange={(e) => { setClientId(e.target.value); setError(''); }}
                placeholder="e.g. 12345_app.ctrader.com"
                className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30 transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Client Secret</label>
              {showSecretInput ? (
                <input
                  type="password"
                  value={clientSecret}
                  onChange={(e) => { setClientSecret(e.target.value); setError(''); }}
                  placeholder="Enter your client secret"
                  className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30 transition-all"
                />
              ) : (
                <div className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-foreground-400 text-sm">
                  Secret stored securely
                </div>
              )}
            </div>
          </div>

          {/* Redirect URI notice */}
          <div className="mb-5 p-3 rounded-lg bg-accent-500/5 border border-accent-500/10">
            <div className="flex items-start gap-2">
              <i className="ri-information-line text-accent-400 text-sm shrink-0 mt-px"></i>
              <div>
                <p className="text-foreground-700 text-xs font-medium mb-1">Register this Redirect URI in your cTrader app</p>
                <p className="text-foreground-600 text-xs break-all font-mono bg-background-50 px-2 py-1 rounded border border-background-200/70">
                  {redirectUri}
                </p>
                <p className="text-foreground-500 text-[11px] mt-1.5">
                  Go to <a href="https://openapi.ctrader.com/apps" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:underline">openapi.ctrader.com/apps</a> &rarr; set Redirect URI to match exactly.
                </p>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-red-400 text-xs flex items-start gap-2">
              <i className="ri-error-warning-line text-sm shrink-0 mt-px"></i>
              <span className="break-all">{error}</span>
            </div>
          )}
          {success && (
            <div className="mb-4 bg-primary-500/10 border border-primary-500/20 rounded-lg px-3 py-2 text-primary-400 text-xs flex items-start gap-2">
              <i className="ri-checkbox-circle-line text-sm shrink-0 mt-px"></i>
              <span>{success}</span>
            </div>
          )}

          <button
            type="button"
            onClick={handleAuthorize}
            className="w-full px-5 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
          >
            <span className="flex items-center justify-center gap-2">
              <i className="ri-shield-check-line"></i>
              Authorize with cTrader
            </span>
          </button>
        </div>
      )}

      {/* ── Connected: Status card + link to Strategies ─────────── */}
      {isConnected && (
        <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-accent-500/10 flex items-center justify-center">
                <i className="ri-shield-keyhole-line text-accent-400 text-sm"></i>
              </div>
              <div>
                <h3 className="text-sm font-heading font-semibold text-foreground-900">Connection Status</h3>
                <p className="text-foreground-500 text-xs mt-0.5">
                  Connected via <span className="text-foreground-700 font-mono">{proxyUrl}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={handleRefresh}
                disabled={refreshLoading}
                className="px-4 py-2 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {refreshLoading ? (
                  <span className="flex items-center gap-1.5"><i className="ri-loader-4-line animate-spin"></i>Refreshing...</span>
                ) : (
                  <span className="flex items-center gap-1.5"><i className="ri-refresh-line"></i>Refresh Now</span>
                )}
              </button>
              <button
                type="button"
                onClick={handleDisconnect}
                className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-link-unlink mr-1.5"></i>Disconnect
              </button>
            </div>
          </div>

          {/* Status metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-3 rounded-lg border bg-background-50 border-background-200/70">
              <p className="text-foreground-500 text-[10px] font-medium uppercase tracking-wide mb-1">Gateway Status</p>
              <p className="text-sm font-heading font-bold text-primary-400 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-400 animate-pulse inline-block"></span>
                Connected
              </p>
            </div>
            <div className="p-3 rounded-lg border bg-background-50 border-background-200/70">
              <p className="text-foreground-500 text-[10px] font-medium uppercase tracking-wide mb-1">Token Expires In</p>
              <p className={`text-sm font-heading font-bold font-mono ${isExpired ? 'text-red-400' : isNearExpiry ? 'text-amber-400' : 'text-foreground-900'}`}>
                {formatTimeLeft(expiresAt)}
              </p>
            </div>
            <div className="p-3 rounded-lg border bg-background-50 border-background-200/70">
              <p className="text-foreground-500 text-[10px] font-medium uppercase tracking-wide mb-1">Last Updated</p>
              <p className="text-sm font-heading font-bold text-foreground-900 font-mono">{formatDateTime(updatedAt)}</p>
            </div>
          </div>

          {/* Connected accounts (compact) */}
          {accounts.length > 0 && (
            <div className="p-3 rounded-lg bg-primary-500/5 border border-primary-500/10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-foreground-800 text-xs font-medium">
                    {accounts.length} account{accounts.length > 1 ? 's' : ''} connected
                  </p>
                  <p className="text-foreground-500 text-[11px] mt-0.5">
                    {accounts.map((a) => a.account_name).join(', ')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/strategies')}
                  className="flex items-center gap-1.5 px-3 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-line-chart-line"></i>
                  View in Strategies
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 text-amber-400 text-xs flex items-start gap-2">
              <i className="ri-error-warning-line text-sm shrink-0 mt-px"></i>
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="bg-primary-500/10 border border-primary-500/20 rounded-lg px-3 py-2 text-primary-400 text-xs flex items-start gap-2">
              <i className="ri-checkbox-circle-line text-sm shrink-0 mt-px"></i>
              <span>{success}</span>
            </div>
          )}

          {/* Debug panel */}
          <div>
            <button
              type="button"
              onClick={() => setShowDebugPanel(!showDebugPanel)}
              className="text-foreground-500 hover:text-foreground-700 text-[11px] flex items-center gap-1 cursor-pointer transition-colors"
            >
              <i className={`ri-bug-line ${showDebugPanel ? 'text-primary-400' : ''}`}></i>
              {showDebugPanel ? 'Hide debug info' : 'Show debug info'}
            </button>

            {showDebugPanel && (
              <div className="mt-3 p-3 rounded-lg bg-background-50 border border-background-200/70 space-y-2">
                {lastPayloadKeys && (
                  <div>
                    <p className="text-foreground-500 text-[11px] font-medium mb-0.5">Payload Keys Found:</p>
                    <p className="text-foreground-700 text-[11px] font-mono break-all">{lastPayloadKeys}</p>
                  </div>
                )}
                {lastLogSummary && (
                  <div>
                    <p className="text-foreground-500 text-[11px] font-medium mb-0.5">WebSocket Log:</p>
                    <p className="text-foreground-700 text-[11px] font-mono break-all">{lastLogSummary}</p>
                  </div>
                )}
                {lastWsRaw && (
                  <div>
                    <p className="text-foreground-500 text-[11px] font-medium mb-0.5">Raw Response (last 500 chars):</p>
                    <pre className="text-foreground-700 text-[10px] font-mono break-all whitespace-pre-wrap bg-background-100 p-2 rounded border border-background-200/70 max-h-40 overflow-auto">
                      {lastWsRaw}
                    </pre>
                  </div>
                )}
                {!lastWsRaw && !lastPayloadKeys && (
                  <p className="text-foreground-500 text-[11px]">No debug data yet. Click Refresh Now to capture.</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}